import { useRef } from "react";
import type { PointerEvent } from "react";
import { detectSwipe } from "../lib/swipe";

/**
 * Horizontal swipe → prev/next navigation for plain (non-grid) views. Spread
 * the returned handlers onto a container. Touch/pen only (desktop has buttons).
 *
 * The decision is made during pointermove — firing as soon as horizontal travel
 * crosses the threshold — rather than on pointerup. Over scrollable content the
 * browser can cancel the pointer stream (no pointerup) once it starts a pan, so
 * waiting for release drops the gesture on exactly those regions. Detecting mid-
 * move fires before any such cancel, and a vertical-dominant gesture simply
 * never crosses the horizontal threshold, so scrolling still works.
 */
export function useSwipeNav(onSwipe: (direction: "prev" | "next") => void) {
  const start = useRef<{ x: number; y: number; fired: boolean } | null>(null);

  const reset = () => { start.current = null; };

  return {
    onPointerDown: (e: PointerEvent) => {
      start.current =
        e.pointerType === "touch" || e.pointerType === "pen"
          ? { x: e.clientX, y: e.clientY, fired: false }
          : null;
    },
    onPointerMove: (e: PointerEvent) => {
      const s = start.current;
      if (!s || s.fired) return;
      const dir = detectSwipe(e.clientX - s.x, e.clientY - s.y);
      if (dir) {
        s.fired = true;
        onSwipe(dir);
      }
    },
    onPointerUp: reset,
    onPointerCancel: reset,
  };
}
